"use client";

import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/lib/hooks/useAuth';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase/firebaseConfig';
import type { Account } from '@/lib/types/account';

interface AccountContextType {
  account: Account | null;
  loading: boolean;
  error: Error | null;
  refreshAccount: () => Promise<void>;
}

const AccountContext = createContext<AccountContextType>({
  account: null,
  loading: true,
  error: null,
  refreshAccount: async () => {},
});

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadAccount = async (userId: string) => {
    try {
      const accountDoc = await getDoc(doc(db, 'accounts', userId));
      if (accountDoc.exists()) {
        setAccount(accountDoc.data() as Account);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load account'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.uid) {
      loadAccount(user.uid);
    } else {
      setAccount(null);
      setLoading(false);
    }
  }, [user]);

  const refreshAccount = async () => {
    if (user?.uid) {
      setLoading(true);
      await loadAccount(user.uid);
    }
  };

  return (
    <AccountContext.Provider value={{ account, loading, error, refreshAccount }}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
} 